import { LitElement, html, TemplateResult, nothing } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { getDeviationClass, GetFormattedLocalDate, tooltipText, isOnline, isAdoptionIndicatorScheduleCardEnabled } from '../../../src/utils/helpers';
import { VoyageVesselDetailsMenu } from './voyage-vessel-details-menu';
import '../../voyage-tooltip/src/voyage-tooltip-proxy';
import '../../voyage-spinner/src/voyage-spinner';
import './voyage-vessel-details-menu';
import './voy-wr-status-tag';
import { StepData } from './voy-wr-status-tag';
import { PlanChangedEvent, VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { VoyageRoutingModel } from '../../../src/models/voyage-routing-model'
import {  RemainingDataToPilot, VesselPerformanceModel } from '../../../src/models/vessel-performance-model';
import { VoyageWeatherRoutingModel } from '../../../src/models/weather-routing-model';
import { ScheduleEntry, SimplifiedPortCall } from '../../../src/models/types/voyage-schedules-model.types';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-tooltip';
import { VoyageBestPracticeRoutesModel } from '../../../src/models/voyage-best-practice-routes-model';
import { vesselOwnRouteReasonPopupStyles } from './styles/voy-vessel-gsis-list-entry-styles';
import { SuggestedRouteEvents } from '../../voyage-suggested-routes/src/types';
import { AlertType, VesselAlertsModel } from '../../../src/models/vessel-alerts-model';
import { convertToLocalOffline, fixDateTimeFormatOffline } from '../../../src/utils/offline-utils'; 
import { VoyageImoModel } from '../../../src/models/voyage-imo-model';
import { getWRStatusText, getWrProgressStatus } from './helper';
import '@maersk-global/mds-components-core';
import "@maersk-global/mds-components-core/mc-step-indicator";
import "@maersk-global/mds-components-core/mc-step-indicator-item";
import { ScheduleChangeEvents } from '../../../src/models/schedule-change-model-impl';
import { EtaRequestDeactivated, ScheduleChangeModel } from '../../../src/models/schedule-change-model';
import { closeEtaNotificationOnTerminalChange } from '../../voyage-eta-change-review/src/helper';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { getPilotPointIndeces } from '../../../src/utils/route-utils';
import { ISIMMINENTRISKENABLED, ISOPENOCCEANROUTEENABLED, ISWEATHERROUTINGENABLED } from '../../../src/models/feature-flag-model-impl';
import { FeatureFlagModel } from '../../../src/models/feature-flag-model';
import { checkIfPilotPointisPassed } from '../../voyage-eta-change-review/src/helper';
import { faro } from '../../../src/utils/rum-utility';

export interface PortsPositionInfo {
    departure: {
        top: number
        bottom: number
    }
    arrival: {
        top: number
        bottom: number
    }
}

type WrStatusLabel = {
    required : boolean,
    text : string
}

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

@customElement('voy-vessel-gsis-list-entry')
export class VoyVesselGsisListEntry extends LitElement
{
    @property({attribute: false}) hasDeclinedRequest: boolean = false;
    @property({attribute: false}) showEtaRequest: boolean = true;
    @property({attribute: false}) etaRequestText: string = '';
    @property({attribute: false}) _weatherRoutingOn: boolean = false;
    @property({attribute: false}) _imminentRiskOn: boolean = false;
    @property({attribute: false})
    get omittedPortCall(): SimplifiedPortCall|null
    {
        return this._omittedPortCall;
    }

    set omittedPortCall(value: SimplifiedPortCall | null) {
        const oldValue = this._omittedPortCall
        this._omittedPortCall = value
        this.requestUpdate('omittedPortCall', oldValue)
        this._omittedPortArrivalTime = ''
        if (value) {
            TerminalsModel!
                .getTerminalLocaltime(value.terminalCode, value.arrivalAt)
                .then((localTime) => {
                    this._omittedPortArrivalTime =
                        GetFormattedLocalDate(localTime)
                })
        }
    }

    @property({ type: Object })
    get scheduleEntry(): ScheduleEntry | null {
        return this._scheduleEntry
    }

    set scheduleEntry(value: ScheduleEntry | null) {
        const oldValue = this._scheduleEntry
        this._scheduleEntry = value
        this.requestUpdate('scheduleEntry', oldValue)
        this._scheduleEntryChanged()
    }

    constructor()
    {
        super();
        this._onJobsChanged = this._onJobsChanged.bind(this);
        this._onPlanChanged = this._onPlanChanged.bind(this);
        this._onTerminalsUpdated = this._onTerminalsUpdated.bind(this);
        this._updateCurrentLegData = this._updateCurrentLegData.bind(this);
        this._getBPRIdList = this._getBPRIdList.bind(this);
        this._renderBprAvailability = this._renderBprAvailability.bind(this);
        this._checkIfBasicPlanAvailable = this._checkIfBasicPlanAvailable.bind(this);
        this.getWRStatus = this.getWRStatus.bind(this);
        this._checkForDeclinedEtaRequest = this._checkForDeclinedEtaRequest.bind(this);
        this._disableEtaRequest = this._disableEtaRequest.bind(this);
        this._enableEtaRequestButton = this._enableEtaRequestButton.bind(this);
        this._updateFeatureFlags = this._updateFeatureFlags.bind(this);

        this._weatherRoutingOn = localStorage.getItem(ISWEATHERROUTINGENABLED) === "true";
        this._imminentRiskOn = localStorage.getItem(ISIMMINENTRISKENABLED) === "true";
        
        if(this._weatherRoutingOn) {
            this.getWRStatus()
        }
    }
    
    private _updateFeatureFlags() {
        const wasEnabled = this._weatherRoutingOn;
        this._weatherRoutingOn = localStorage.getItem(ISWEATHERROUTINGENABLED) === "true";
        this._imminentRiskOn = localStorage.getItem(ISIMMINENTRISKENABLED) === "true";
        // If feature flag just got enabled, fetch WR status
        if (!wasEnabled && this._weatherRoutingOn) {
            this.getWRStatus();
        }
    }

    private _disableEtaRequest() {
        if (!this._scheduleEntry?.active) return;
        this.showEtaRequest = false;
        this.etaRequestText = EtaRequestDeactivated.RequestFromTbm;
    }

    private _enableEtaRequestButton() {
        if (!this._scheduleEntry?.active) return;
        this.showEtaRequest = true;
        this.etaRequestText = '';
    }
    
    private renderWRStatus() {
        if (this._wrStatusLoading) {
            return html`
                <div class="wr-status-container">
                    <voy-wr-status-tag 
                        .loading=${true}>
                    </voy-wr-status-tag>
                </div>
            `;
        } else {
            if(!this._plan || this._plan.eta < new Date().getTime()) return html``;
            const hasWrProgressData = this._wrProgressData?.length > 0;
            if(hasWrProgressData) {
                return html`
                <div class="wr-status-container">
                    <voy-wr-status-tag 
                        .required=${true}
                        .statusText=${""}
                        .timestamp=${""}
                        .loading=${false}
                        .showIcon=${true}
                        .progressData=${this._wrProgressData}>
                    </voy-wr-status-tag>
                </div>`
            } else {
                return html`
                <div class="wr-status-container">
                    <voy-wr-status-tag
                        .required=${this._wrStatusLabel.required}
                        .statusText=${this._wrStatusLabel.text}
                        .showIcon=${this._wrStatusLabel.required}
                        .progressData=${[]}>
                    </voy-wr-status-tag>
                </div>
                `
            }
        }
    }

    private renderStarRouteAdoptionStatus(): TemplateResult {
        if (!isAdoptionIndicatorScheduleCardEnabled() || this._planStatus !== 'approved' || !this._bprAvaliability) {
            return html``;
        }

        const routeSelection = this._planObject?.metadata?.routeSelection;
        const etd = this._planObject?.etd;
        const etdTimestamp = typeof etd === 'string' ? new Date(etd).getTime() : etd;
        const createdAt = routeSelection?.createdAtFrontendUnixUTC;
        if (!routeSelection || typeof etdTimestamp !== 'number' || !Number.isFinite(etdTimestamp) ||
            typeof createdAt !== 'number' || !Number.isFinite(createdAt) || createdAt >= etdTimestamp) {
            return html``;
        }

        const status = routeSelection.isMoreFuelEfficientThanStarRoute
            ? 'Out Performing StarRoute'
            : routeSelection.isAlignedToStarRoute
                ? 'Aligned to Star Route'
                : 'Not Aligned to Star Route';
        const statusClass = routeSelection.isMoreFuelEfficientThanStarRoute || routeSelection.isAlignedToStarRoute
            ? 'aligned'
            : 'deviation';

        return html`
            <div class="route-selection-status">
                <span class="route-selection-status__text ${statusClass}">${status}</span>
            </div>
        `;
    }

    static get styles()
    {
        return vesselOwnRouteReasonPopupStyles
    }

    private _renderBprAvailability(isLoading: boolean, bprAvailability: boolean, oorAvailability: boolean): TemplateResult {
        const selectedRoute = this._planObject?.currentSelectedRouteId
            ? this._planObject.routes.find(route => route.routeId === this._planObject?.currentSelectedRouteId)
            : null;

        if (this._planStatus === 'approved' && selectedRoute) {
            if (selectedRoute.routeCreatedBy == "bridge") return html`<span>Own route selected</span>`;

            return html`<div class="selected-route"><span class="selected-route--name">${selectedRoute.name}</span> selected</div>`;
        }

        if (isLoading) {
            return html`<span>Loading...</span>`;
        }

        if (bprAvailability && oorAvailability) {
            return html`<span>Star Route & Open Ocean available</span>`;
        }
        if (oorAvailability) {
            return html`<span>Open Ocean available</span>`;
        }
        return bprAvailability
            ? html`<span>Star Route available</span>`
            : html`<span>No Star Route available</span>`;
    }



    render()
    {
        const terminalsExists = (TerminalsModel!.getTerminal(this.scheduleEntry!.departureTerminalCode) !== undefined ||
                                 TerminalsModel!.isPseudoTerminal(this.scheduleEntry!.departureTerminalCode)) &&
                                (TerminalsModel!.getTerminal(this.scheduleEntry!.arrivalTerminalCode) !== undefined ||
                                 TerminalsModel!.isPseudoTerminal(this.scheduleEntry!.arrivalTerminalCode));
        const origArrTerm = this._scheduleEntry!.arrivalPortCall!.GSIS && this._scheduleEntry!.arrivalPortCall!.GSIS.terminalCode;
        const origDepTerm = this._scheduleEntry!.departurePortCall!.GSIS && this._scheduleEntry!.departurePortCall!.GSIS.terminalCode;
        const etdInUTC = TerminalsModel!.isPseudoTerminal(this.scheduleEntry!.departureTerminalCode);
        const etaInUTC = TerminalsModel!.isPseudoTerminal(this.scheduleEntry!.arrivalTerminalCode);

        const depVoyNumber = this._scheduleEntry!.departurePortCall?.GSIS?.departureVoyage;
        const arrVoyNumber = this._scheduleEntry!.arrivalPortCall?.GSIS?.arrivalVoyage;
        return html`
        <div class="container" ?planning=${this._planStatus === "planning" || this._planStatus === "scheduleChanged"} ?approved=${this._planStatus === "approved"}>
            <div class="starRoute" ?avaliable=${this._bprAvaliability || this._oorAvailability}>
                ${this._renderBprAvailability(this._bprAvaliabilityLoading, this._bprAvaliability, this._oorAvailability)}
            </div>
            ${this._recalculating
                ? html`<div class="recalculating">Calculating...</div>`
                : null}
            ${this.renderStarRouteAdoptionStatus()}
            ${this._weatherRoutingOn ? this.renderWRStatus() : nothing}
            <voyage-vessel-details-menu
                .plan=${this._plan}
                .planStatus=${this._planStatus}
                .scheduleEntry=${this.scheduleEntry}
                .recalculating=${this._recalculating}
                .showEtaRequest=${this.showEtaRequest} 
                .showEtaRequestButton=${this.scheduleEntry?.active} 
                .etaRequestText=${this.etaRequestText}
            ></voyage-vessel-details-menu>
            ${origDepTerm &&
            origDepTerm != this._scheduleEntry!.departureTerminalCode
                ? html`
                      <div data-test="omittedportname" class="omittedPortName">
                          ${TerminalsModel!.getCity(origDepTerm) || origDepTerm}
                      </div>
                      <div></div>
                  `
                : html``}

            <div data-test="depport" class="port">
                ${TerminalsModel!.getCity(
                    this._scheduleEntry!.departureTerminalCode,) ?? '-'} (${this._scheduleEntry!.departureTerminalCode ?? '-'})
            </div>
            <p class="voyage-number">Dep Voy No: <strong>${depVoyNumber ?? '-'}</strong></p>
            ${this._renderPortNotification(
                origDepTerm,
                this._scheduleEntry!.departureTerminalCode,
            )}
            <div class="timeAndEditWrapper">
                <div data-test="deplbl" class="timeHeader">Departure (${this._getPlanSource(this._etd, this._etdCommunicated)})</div> 
                <div data-test="deppofflbl" class="timeHeader">Pilot Off (${this._getPlanSource(this._pilotDeparture, this._pilotDepartureCommunicated)})</div>
            </div>

            <div class="timeAndEditWrapper">
                <div data-test="depts" class="time timestm">${this._renderCommunicatedTime(this._etdCommunicated, etdInUTC)}</div>
                <div data-test="deppofflbl" class="time editBtnWrapper">
                    <span class="timestm">${this._renderCommunicatedTime(this._pilotDepartureCommunicated, etdInUTC)}</span>
                        ${ this._etdSource !== "actual" || this._pilotDepartureSource !== "actual" ?
                        html `
                            ${ terminalsExists ? html`
                                <button data-test="editdeporpoffts" class="editButton departureTim" @click=${() => this._emitGsisEvent("edit-departure-times")}>Edit</button>
                                ` : html`
                                <button  data-test="adddepterminal" class="editButton add-terminal add-departure-terminal" @click=${() => this._emitGsisEvent("add-terminal")}>Add Terminal</button>
                            `}    
                        ` : html``}
                </div>
            </div>

            <div class="timeAndEditWrapper bottomMargin">
                <button data-test="depts" class="time-schedules label ${getDeviationClass(this._etd, this._etdCommunicated)}" .disabled=${!this.hasDeclinedRequest}><span>${SvgIcons.lightExclamationCircle}
                                    <voyage-tooltip-proxy
                                    text=${this._etdSource == 'actual' ? tooltipText.actual : tooltipText.schedule}
                                    html-content
                                    .positionPriority=${["bottom"]}>
                                    </voyage-tooltip-proxy>
                                </span>${this._renderScheduledTime(this._etd, this._etdSource, etdInUTC)}</button>
                <button data-test="deppofflbl" class="editBtnWrapper">
                    <span class="time-schedules label ${getDeviationClass(this._pilotDeparture, this._pilotDepartureCommunicated)}" .disabled=${!this.hasDeclinedRequest}><span>${SvgIcons.lightExclamationCircle}
                                    <voyage-tooltip-proxy
                                    text=${this._pilotDepartureSource == 'actual' ? tooltipText.actual : tooltipText.schedule}
                                    html-content
                                    .positionPriority=${["bottom"]}>
                                    </voyage-tooltip-proxy>
                                </span>${this._renderScheduledTime(this._pilotDeparture, this._pilotDepartureSource, etdInUTC)}</span>
                </button>
            </div>


            ${this._renderPortOmit()}

            ${origArrTerm && origArrTerm != this._scheduleEntry!.arrivalTerminalCode ? html`
            <div  data-test="omittedPortName" class="omittedPortName">${TerminalsModel!.getCity(origArrTerm) || origArrTerm}</div><div></div>
            `:html``}

            <div data-test="arrport" class="port">${TerminalsModel!.getCity(this._scheduleEntry!.arrivalTerminalCode) ?? '-'} (${(this._scheduleEntry!.arrivalTerminalCode) ?? '-'})</div>
            ${this._renderPortNotification(origArrTerm, this._scheduleEntry!.arrivalTerminalCode)}
            <p class="voyage-number">Arr Voy No: <strong>${arrVoyNumber ?? '-'}</strong></p>

            <div class="timeAndEditWrapper">
                <div data-test="arrponlbl" class="timeHeader">Pilot St. (${this._getPlanSource(this._pilotArrival, this._pilotArrivalCommunicated)})</div>
                <div data-test="arrlbl" class="timeHeader">Arrival (${this._getPlanSource(this._eta, this._etaCommunicated)})</div>
            </div>

            <div class="timeAndEditWrapper">
                <div data-test="arrponts" class="time timestm" >${this._renderCommunicatedTime(this._pilotArrivalCommunicated, etaInUTC)}</div>
                <div data-test="arrts" class="time editBtnWrapper">
                    <span class="timestm">${this._renderCommunicatedTime(this._etaCommunicated, etaInUTC)}</span>
                    ${ this._etaSource !== "actual" || this._pilotArrivalSource !== "actual" ?
                    html `
                        ${ terminalsExists ? html`
                            <button data-test="editarrts" class="editButton"  @click=${() => this._emitGsisEvent("edit-arrival-times")}>Edit</button>
                        ` : html`
                            <button data-test="addarrterminal" class="editButton add-terminal add-arrival-terminal" @click=${() => this._emitGsisEvent("add-terminal")}>Add Terminal</button>
                        `}`
                    : html ``
                }
                </div>
            </div>

            <div class="timeAndEditWrapper bottomMargin">
                <button data-test="depts" class="time-schedules label ${getDeviationClass(this._pilotArrival, this._pilotArrivalCommunicated)}" .disabled=${!this.hasDeclinedRequest} @click=${this.openEtaChangeModal}><span>${SvgIcons.lightExclamationCircle}
                                    <voyage-tooltip-proxy
                                    text=${this._pilotArrivalSource == 'actual' ? tooltipText.actual : this.hasDeclinedRequest ? tooltipText.declined : tooltipText.schedule}
                                    html-content
                                    .positionPriority=${["bottom"]}>
                                    </voyage-tooltip-proxy>
                                </span>${this._renderScheduledTime(this._pilotArrival, this._pilotArrivalSource, etaInUTC, this.hasDeclinedRequest)}</button>
                <button data-test="deppofflbl" class="editBtnWrapper" .disabled=${!this.hasDeclinedRequest} @click=${this.openEtaChangeModal}>
                <span class="time-schedules label ${getDeviationClass(this._eta, this._etaCommunicated)}"><span>${SvgIcons.lightExclamationCircle}
                                    <voyage-tooltip-proxy
                                    text=${this._etaSource == 'actual' ? tooltipText.actual : this.hasDeclinedRequest ? tooltipText.declined : tooltipText.schedule}
                                    html-content
                                    .positionPriority=${["bottom"]}>
                                    </voyage-tooltip-proxy>
                                </span>${this._renderScheduledTime(this._eta, this._etaSource, etaInUTC, this.hasDeclinedRequest)}</span>
                </button>
            </div>

            <div class="drafts-container">
                <div class="drafts-titles-imminent">
                    <div>Fore Draft</div>
                    <div>Aft Draft</div>
                    <div>Fluid GM</div>
                    <div>Solid GM</div>
                    
                </div>
                <div
                    class="drafts-values ${this._planStatus
                        ? 'hasPlanStatus'
                        : ''}"
                >
                    <div data-test="forwardDraft">${this._forwardDraft}</div>
                    <div data-test="aftDraft">${this._aftDraft}</div>
                    <div data-test="GMt" class="editBtnWrapperGMT">
                    <span class="Gmt">${this._GMt}</span>
                    <span class="solidGm">${this._solidGM}</span>
                       
                        ${this._etaSource !== 'actual' &&
                        this._pilotArrivalSource !== 'actual'
                            ? html`<button
                                  class="editButton"
                                  @click=${() =>
                                      this._emitGsisEvent('edit-drafts')}
                              >
                                  Edit
                              </button>`
                            : html``}
                    </div>
                </div>
            </div>
            ${ this._scheduleEntry && this._scheduleEntry.active
            ? html`
                <div class="pilot-container">
                    <div class="pilot-titles">
                        <div>Time to pilot</div>
                        <div>Distance to pilot</div>
                        <div>Avg. speed to pilot</div>
                    </div>
                    <div class="pilot-values">
                        <div data-test='timeToPilot'>${this._remainingDataToPilot && this._remainingDataToPilot.timeToPilotEta ? this._getDeltaTimeText(this._remainingDataToPilot.timeToPilotEta) : "-"}</div>
                        <div data-test='distanceToPilot'>${this._remainingDataToPilot && this._remainingDataToPilot.distanceToPilot ? this._remainingDataToPilot.distanceToPilot.toFixed(1) + " NM" : "-"}</div>
                        <div data-test='avgSpeedToPilot'>${this._remainingDataToPilot && this._remainingDataToPilot.avgSpeedToPilotEta ? this._remainingDataToPilot.avgSpeedToPilotEta.toFixed(1) + " kt" : "-"}</div>
                    </div>
                </div>`
            : html``}
           
        </div>`;
    }

    connectedCallback() {
        super.connectedCallback()

        VoyageRoutingModel!.addEventListener("jobs-changed", this._onJobsChanged);
        VesselPlanningModel!.addEventListener(SuggestedRouteEvents.PlanChanged, <EventListener>this._onPlanChanged);
        TerminalsModel!.addEventListener('terminals-updated', this._onTerminalsUpdated);
        VesselPlanningModel!.addEventListener(SuggestedRouteEvents.RecalcParametersChanged,(evt) => this._onRecalcParametersChanged(evt as CustomEvent));
        FeatureFlagModel?.addEventListener('featureFlagsUpdated', this._updateFeatureFlags);
        ScheduleChangeModel?.addEventListener(ScheduleChangeEvents.DeclineAlert, this._checkForDeclinedEtaRequest as unknown as EventListener);
        ScheduleChangeModel?.addEventListener(ScheduleChangeEvents.DisableEtaRequest, this._disableEtaRequest as unknown as EventListener);
        ScheduleChangeModel?.addEventListener(ScheduleChangeEvents.EnableEtaRequestButton, this._enableEtaRequestButton as unknown as EventListener);

        if (this._scheduleEntry && this._scheduleEntry.active)
        {
            VoyageBestPracticeRoutesModel!.addEventListener("bpr-lists-updated", this._getBPRIdList);
            VesselPerformanceModel!.addEventListener('executing-performance-data-updated', this._updateCurrentLegData);
            ScheduleChangeModel?.addEventListener(ScheduleChangeEvents.DeclineAlert, this._checkForDeclinedEtaRequest as unknown as EventListener);
            this._updateCurrentLegData();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback()

        if (this._options) {
            this._options.remove()
            this._options = null
        }
        
        FeatureFlagModel?.removeEventListener('featureFlagsUpdated', this._updateFeatureFlags);
        VoyageRoutingModel!.removeEventListener("jobs-changed", this._onJobsChanged);
        VesselPlanningModel!.removeEventListener(SuggestedRouteEvents.PlanChanged, <EventListener>this._onPlanChanged);
        TerminalsModel!.removeEventListener('terminals-updated', this._onTerminalsUpdated);
        VesselPlanningModel!.removeEventListener('executing-performance-data-updated', this._updateCurrentLegData);
        VoyageBestPracticeRoutesModel!.removeEventListener("bpr-lists-updated", this._getBPRIdList);
        ScheduleChangeModel?.removeEventListener(ScheduleChangeEvents.DeclineAlert, this._checkForDeclinedEtaRequest as unknown as EventListener);
        ScheduleChangeModel?.removeEventListener(ScheduleChangeEvents.DisableEtaRequest, this._disableEtaRequest as unknown as EventListener);
        ScheduleChangeModel?.removeEventListener(ScheduleChangeEvents.EnableEtaRequestButton, this._enableEtaRequestButton as unknown as EventListener);
    }

    private async _alignSelectedRouteIdForSchedule(plan: VoyagePlan | null) {
        if (!plan) return
        // align currentSelectedRouteId with selectedRouteIndex
        if (
            plan?.selectedRouteIndex > -1 &&
            plan?.routes[plan?.selectedRouteIndex]?.routeId !==
                plan?.currentSelectedRouteId
        ) {
            plan.currentSelectedRouteId =
                plan.routes[plan.selectedRouteIndex].routeId
            await VesselPlanningModel!.setPlan(plan.scheduleId, plan, false)
        }
    }

    private _getBPRIdList() {
        if (
            this._scheduleEntry &&
            this._scheduleEntry?.id &&
            this._scheduleEntry?.departureTerminalCode &&
            this._scheduleEntry?.arrivalTerminalCode
        ) {
            const bprAvaliability =
                VoyageBestPracticeRoutesModel!.getBPRAvaliabilityPerSchedule(
                    this._scheduleEntry!.id,
                    this._scheduleEntry!.departureTerminalCode,
                    this._scheduleEntry!.arrivalTerminalCode,
                )
            this._bprAvaliability = !!bprAvaliability
            this._bprAvaliabilityLoading = false
            this._oorAvailability =
                localStorage.getItem(ISOPENOCCEANROUTEENABLED) === 'true' &&
                !!this._scheduleEntry?.isOORAvailable;
        } else {
            console.log('No schedule entry found')
        }
    }

    private async getWRStatus() {
        if (!this._plan) {
            this._wrStatusLabel.required = false;
            this._wrStatusLoading = false;
            return;
        };
    
        let weatherRoutingInfo;
    
        if (this._plan.status === 'approved') {
            weatherRoutingInfo = this._plan.routes[this._plan.selectedRouteIndex]?.weatherRoutingInfo;
        } else {
            weatherRoutingInfo = this._plan.routes.find(route => route?.weatherRoutingInfo?.required)?.weatherRoutingInfo;
        }
    
        if (weatherRoutingInfo) {
            this._wrStatusLabel.required = true;
            this._wrStatusLabel.text = getWRStatusText(weatherRoutingInfo);
            
            // Fetch progress data if we have an audit ID
            const auditId = this._plan.audit_id;
            if (auditId && VoyageWeatherRoutingModel) {
                try {
                    const progressData = await Promise.race([
                        VoyageWeatherRoutingModel.getWRProgress(auditId),
                        new Promise<StepData[]>((_, reject) => {
                            setTimeout(() => reject(new Error('Weather Routing progress request timed out')), 10000);
                        }),
                    ]);
                    if (progressData && Array.isArray(progressData)) {
                        this._wrProgressData = progressData;
                    }
                } catch (error) {
                    console.warn('Error fetching Weather Routing progress data:', error);
                    this._wrProgressData = [];
                }
            }
        }
    
        this._wrStatusLoading = false;
    }

    // Returns the internal offsets of the left-hand dots for the ports
    async getPortsPositionInfo(): Promise<PortsPositionInfo> {
        await this.updateComplete

        const thisRect = this.getBoundingClientRect()
        const ports = this.shadowRoot!.querySelectorAll('.port')
        const depRect = ports[0].getBoundingClientRect()
        const arrRect = ports[1].getBoundingClientRect()

        // The dots are 6 pixels down and 8 pixels high from the port name
        return {
            departure: {
                top: depRect.top + 6 - thisRect.top,
                bottom: depRect.top + 14 - thisRect.top,
            },
            arrival: {
                top: arrRect.top + 6 - thisRect.top,
                bottom: arrRect.top + 14 - thisRect.top,
            },
        }
    }

    @property({attribute: false}) private _planStatus: string = "";
    @property({attribute: false}) private _plan: VoyagePlan | null = null;
    @property({attribute: false}) private _planObject: VoyagePlan | null = null;
    @property({attribute: false}) private _etd: string = "-";
    @property({attribute: false}) private _etdCommunicated: string = "-";
    @property({attribute: false}) private _etdSource: string = "Sch";
    @property({attribute: false}) private _pilotDeparture: string = "-";
    @property({attribute: false}) private _pilotDepartureCommunicated: string = "-";
    @property({attribute: false}) private _pilotDepartureSource: string = "Sch";
    @property({attribute: false}) private _eta: string = "-";
    @property({attribute: false}) private _etaCommunicated: string = "-";
    @property({attribute: false}) private _etaSource: string = "Sch";
    @property({attribute: false}) private _pilotArrival: string = "-";
    @property({attribute: false}) private _pilotArrivalCommunicated: string = "-";
    @property({attribute: false}) private _pilotArrivalSource: string = "Sch";
    @property({attribute: false}) private _recalculating: boolean = false;
    @property({attribute: false}) private _omittedPortCall: SimplifiedPortCall|null = null;
    @property({attribute: false}) private _omittedPortArrivalTime: string = "";
    @property({attribute: false}) private _forwardDraft: string = "-";
    @property({attribute: false}) private _aftDraft: string = "-";
    @property({attribute: false}) private _GMt: string = "-";
    @property({attribute: false}) private _solidGM: string = "-";
    @property({attribute: false}) private _remainingDataToPilot: RemainingDataToPilot|null = null;
    @property({attribute: false}) private _bprAvaliability: boolean = false;
    @property({attribute: false}) private _oorAvailability: boolean = false;
    @property({ attribute: false }) private _wrStatusLabel: WrStatusLabel = {
        required: false,
        text: '',
    }
    @property({ attribute: false }) private _wrProgressData: StepData[] = [];
    @property({attribute: false}) private _bprAvaliabilityLoading: boolean = true;
    @property({attribute: false}) private _wrStatusLoading: boolean = true;

    private _options: VoyageVesselDetailsMenu | null = null
    private _scheduleEntry: ScheduleEntry | null = null

    private _renderPortOmit(): TemplateResult {
        if (!this._omittedPortCall) {
            return html``
        }

        return html`
            <div class="omittedPortName">
                ${TerminalsModel!.getCity(this._omittedPortCall.terminalCode) ||
                this._omittedPortCall.terminalCode}
            </div>
            <div class="portNotification omitColor">PORT OMIT</div>

            <div class="timeHeader strikethrough">Pilot St. (Sch)</div>
            <div class="timeHeader strikethrough">Arrival (Sch)</div>
            
            <div class="time strikethrough bottomMargin">-</div>
            <div class="time strikethrough bottomMargin">
                ${this._omittedPortArrivalTime}
            </div>
        `
    }

    private _renderPortNotification(
        origTerminal: string | null,
        terminal: string,
    ) {
        if (origTerminal === null) {
            // Port inducement
            return html`<div
                data-test="portnotification-add"
                class="portNotification inducementColor"
            >
                PORT INDUCEMENT
            </div>`
        } else if (origTerminal !== terminal) {
            // Port change
            return html`<div
                data-test="portnotification-change"
                class="portNotification changeColor"
            >
                PORT CHANGE
            </div>`
        }

        return html`<div></div>`
    }

    private _renderCommunicatedTime(value: string, inUTC: boolean): TemplateResult
    {
        const hasValue = value !== "-";
        return html`${value}${hasValue ? (inUTC ? html` <span class="utcWarn">UTC</span>` : html` <span>LT</span>`) : ''}`;
    }

    private _renderScheduledTime(value: string, source: string, inUTC: boolean, hasDeclinedRequest?: boolean): TemplateResult
    {
        const hasValue = value !== "-";
        if (!hasValue) {
            return html`${value}`;
        }
        return html`${value}${inUTC ? html` <span class="utcWarn">UTC</span>` : html` <span>LT</span>`}${source === "actual" ? html`- <span class="green-bold">Act</span>` : html`- <span class="green-bold">Sch</span>`}${hasDeclinedRequest ? SvgIcons.linkIcon : nothing}`;
    }
     
    private async _scheduleEntryChanged()
    {
        if (this._scheduleEntry)
        {
            let etd = this._scheduleEntry.departurePortCall?.GSIS?.departureAt;
            this._etdSource = this._getDepartureScheduleSource(this._scheduleEntry);
            if(this._etdSource == 'actual'){
                etd = this._scheduleEntry.departurePortCall?.GSIS?.actuals?.departureAt ?? etd;
            }
            let eta = this._scheduleEntry.arrivalPortCall?.GSIS?.arrivalAt;
            this._etaSource = this._getArrivalScheduleSource(this._scheduleEntry);
            if(this._etaSource == 'actual'){
                eta = this._scheduleEntry.arrivalPortCall?.GSIS?.actuals?.arrivalAt ?? eta;
            }

            let departurePilotEtd = isVesselOnCloud ? (this._scheduleEntry.departurePortCall?.GSIS?.estimatedPilotDropOff ?? null) : (this._scheduleEntry.departurePortCall?.GSIS?.estimatedpilotdropoff ?? null);
            let arrivalPilotEta = isVesselOnCloud ? (this._scheduleEntry.arrivalPortCall?.GSIS?.estimatedPilotBoarding ?? null) : (this._scheduleEntry.arrivalPortCall?.GSIS?.estimatedpilotboarding ?? null);

            let departurePilotEtdCommunicated = null;
            let arrivalPilotEtaCommunicated = null;
            let etdCommunicated = null;
            let etaCommunicated = null;

            let pilotDepartureSource = this._getDepartureScheduleSource(this._scheduleEntry, true);
            let pilotArrivalSource = this._getArrivalScheduleSource(this._scheduleEntry, true);

            if (this._scheduleEntry.active)
            {
                const origArrivalTerminal = this._scheduleEntry!.arrivalPortCall!.GSIS && this._scheduleEntry!.arrivalPortCall!.GSIS.terminalCode;
                closeEtaNotificationOnTerminalChange(origArrivalTerminal, this._scheduleEntry!.arrivalTerminalCode);
                this._updateCurrentLegData();
                this._checkIfBasicPlanAvailable(this._scheduleEntry);
                this._checkIfPilotPointisPassed(this.scheduleEntry!.id)
                const currentScheduleChangeAlert = ScheduleChangeModel?.getCurrentAlert();
                if (currentScheduleChangeAlert) {
                    await ScheduleChangeModel?.checkForDeclinedRequest(currentScheduleChangeAlert);
                }
            }

            try {
                const plan = await VesselPlanningModel!.getCurrentPlan(
                    this._scheduleEntry.id,
                )
                if (plan) {
                    // adding observability for plan having status as approved but not having foreDraft, afterDraft , gmt, solid gm, reefers values. 
                    if(plan.status === 'approved' && (plan.foreDraft === undefined || plan.aftDraft === undefined || plan.gmt === undefined || plan.solidGm === undefined || plan.selectedRouteIndex === -1)){
                        faro.api.pushEvent("approved-plan-with-missing-values", {
                            imo: `${plan.imoNo}`,
                            selectedRouteIndex: `${plan.selectedRouteIndex}`,
                            trigger: "Has approved plan status but missing values",
                        });
                    }

                    this._plan = plan
                    this._planStatus = plan.status;
                    this._planObject = plan;
                    if (this._scheduleEntry.arrivalTerminalCode !== plan.arrivalTerminalCode ||
                        this._scheduleEntry.departureTerminalCode !== plan.departureTerminalCode) {
                        this._planStatus = "scheduleChanged";
                    }
                    if (plan.departurePilotEtd > 0)
                    {
                        departurePilotEtdCommunicated = new Date(plan.departurePilotEtd).toISOString();
                        pilotDepartureSource = this._getDepartureScheduleSource(this._scheduleEntry, true);
                    }
                    if (plan.arrivalPilotEta > 0)
                    {
                        arrivalPilotEtaCommunicated = new Date(plan.arrivalPilotEta).toISOString();
                        pilotArrivalSource = this._getArrivalScheduleSource(this._scheduleEntry, true);
                    }
                    if (plan.etd > 0){
                        etdCommunicated = new Date(plan.etd).toISOString();
                    }
                    if (plan.eta > 0){
                        etaCommunicated = new Date(plan.eta).toISOString()
                    }
                    this._forwardDraft = plan.foreDraft > 0 ? `${plan.foreDraft} m` : "-";
                    this._aftDraft = plan.aftDraft > 0 ? `${plan.aftDraft} m` : "-";
                    this._GMt = plan.gmt == null ? "-" : `${plan.gmt} m`;
                    this._solidGM = plan.solidGm == null ? "-" : `${plan.solidGm} m`;
                    this._alignSelectedRouteIdForSchedule(plan);
                    if (this._weatherRoutingOn) {
                        this.getWRStatus();
                    }
                } else {
                    this._planStatus = ''
                }
            } catch (err) {
                // TODO: Make this visible in some way (alerting to shore or to user)
                console.warn(
                    `Failed to load plan for schedule entry ${this._scheduleEntry.id}`,
                    err,
                )
            }

            // Handle departure pilot timing from actuals
            if (this._scheduleEntry.departurePortCall?.current?.actuals?.pilotOffAt)
            {
                departurePilotEtd = this._scheduleEntry.departurePortCall.current.actuals.pilotOffAt;
                pilotDepartureSource = "actual";
            }

            // Handle arrival pilot timing from actuals
            const arrivalActuals = this._scheduleEntry.arrivalPortCall?.current?.actuals;
            if (arrivalActuals)
            {
                if (isVesselOnCloud && arrivalActuals.pilotOnAt)
                {
                    arrivalPilotEta = arrivalActuals.pilotOnAt;
                    pilotArrivalSource = "actual";
                }
                else if (!isVesselOnCloud && arrivalActuals.pilotStationArrivalAt)
                {
                    arrivalPilotEta = arrivalActuals.pilotStationArrivalAt;
                    pilotArrivalSource = "actual";
                }
            }

            // If neither actual nor communicated pilot timings were available, try to get estimated pilot timings
            // if (!departurePilotEtd && etd) {
            //     try {
            //         departurePilotEtd = this._scheduleEntry.departurePortCall && this._scheduleEntry.departurePortCall?.GSIS?.estimatedPilotDropOff || null;
            //     } catch (err) {
            //         // TODO: Make this visible in some way (alerting to shore or to user)
            //         console.warn(`Failed to load pilot timings for terminal ${this._scheduleEntry.departureTerminalCode}`, err);
            //     }
            // }
            // if (!arrivalPilotEta && eta) {
            //     try {
            //         arrivalPilotEta = this._scheduleEntry.arrivalPortCall && this._scheduleEntry.arrivalPortCall?.GSIS?.estimatedPilotBoarding || null;
            //     } catch (err) {
            //         // TODO: Make this visible in some way (alerting to shore or to user)
            //         console.warn(`Failed to load pilot timings for terminal ${this._scheduleEntry.arrivalTerminalCode}`, err);
            //     }
            // }

            if (!departurePilotEtd && etd) {
                try {
                    const depPilotInfo =
                        await TerminalsModel!.getTerminalPilotInfo(
                            this._scheduleEntry.departureTerminalCode,
                        )
                    if (depPilotInfo.timeToPilotInSeconds) {
                        departurePilotEtd = new Date(
                            new Date(etd).getTime() +
                                depPilotInfo.timeToPilotInSeconds * 1000,
                        ).toISOString()
                    }
                } catch (err) {
                    // TODO: Make this visible in some way (alerting to shore or to user)
                    console.warn(
                        `Failed to load pilot timings for terminal ${this._scheduleEntry.departureTerminalCode}`,
                        err,
                    )
                }
            }
            if (!arrivalPilotEta && eta) {
                try {
                    const arrPilotInfo =
                        await TerminalsModel!.getTerminalPilotInfo(
                            this._scheduleEntry.arrivalTerminalCode,
                        )
                    if (arrPilotInfo.timeToBerthInSeconds) {
                        arrivalPilotEta = new Date(
                            new Date(eta).getTime() -
                                arrPilotInfo.timeToBerthInSeconds * 1000,
                        ).toISOString()
                    }
                } catch (err) {
                    // TODO: Make this visible in some way (alerting to shore or to user)
                    console.warn(
                        `Failed to load pilot timings for terminal ${this._scheduleEntry.arrivalTerminalCode}`,
                        err,
                    )
                }
            }

            
            const timestampsAndTerminals = [];
            if(etd)
                timestampsAndTerminals.push({ timestamp: fixDateTimeFormatOffline(etd), terminalId: this._scheduleEntry.departureTerminalCode });

            if(eta)
                timestampsAndTerminals.push({ timestamp: fixDateTimeFormatOffline(eta), terminalId: this._scheduleEntry.arrivalTerminalCode })

            if (departurePilotEtd)
            {
                timestampsAndTerminals.push({ timestamp: fixDateTimeFormatOffline(departurePilotEtd), terminalId: this._scheduleEntry.departureTerminalCode });
            }
            if (arrivalPilotEta)
            {
                timestampsAndTerminals.push({ timestamp: fixDateTimeFormatOffline(arrivalPilotEta), terminalId: this._scheduleEntry.arrivalTerminalCode });
            }
            if (departurePilotEtdCommunicated)
                timestampsAndTerminals.push({ timestamp: fixDateTimeFormatOffline(departurePilotEtdCommunicated), terminalId: this._scheduleEntry.departureTerminalCode });

            if (arrivalPilotEtaCommunicated)
                timestampsAndTerminals.push({ timestamp: fixDateTimeFormatOffline(arrivalPilotEtaCommunicated), terminalId: this._scheduleEntry.arrivalTerminalCode });

            if (etdCommunicated)
                timestampsAndTerminals.push({ timestamp: fixDateTimeFormatOffline(etdCommunicated), terminalId: this._scheduleEntry.departureTerminalCode });

            if(etaCommunicated)
                timestampsAndTerminals.push({ timestamp: fixDateTimeFormatOffline(etaCommunicated), terminalId: this._scheduleEntry.arrivalTerminalCode });

            let conversionResult: string[] = [];
            if(isVesselOnCloud && !isOnline()) {
                for (const timestampAndTerminal of timestampsAndTerminals) {
                    const result = await convertToLocalOffline(timestampAndTerminal.timestamp, timestampAndTerminal.terminalId);
                    if(result) {
                        conversionResult.push(result);
                    }
                }
            } else {
                conversionResult = await TerminalsModel!.getTerminalLocaltimeBatch(timestampsAndTerminals);
            }

            let idx = 0;
            this._etd = etd ? GetFormattedLocalDate(conversionResult[idx++]) : "-";
            this._eta = eta ? GetFormattedLocalDate(conversionResult[idx++]) : "-";
            this._pilotDeparture = departurePilotEtd ? GetFormattedLocalDate(conversionResult[idx++]) : "-";
            this._pilotArrival = arrivalPilotEta ? GetFormattedLocalDate(conversionResult[idx++]) : "-";
            this._pilotDepartureCommunicated = departurePilotEtdCommunicated ? GetFormattedLocalDate(conversionResult[idx++]) : "-";
            this._pilotArrivalCommunicated = arrivalPilotEtaCommunicated ? GetFormattedLocalDate(conversionResult[idx++]) : "-";
            this._etdCommunicated = etdCommunicated ? GetFormattedLocalDate(conversionResult[idx++]) : "-";
            this._etaCommunicated = etaCommunicated ? GetFormattedLocalDate(conversionResult[idx++]) : "-";
            this._getBPRIdList()
            this._pilotDepartureSource = pilotDepartureSource;
            this._pilotArrivalSource = pilotArrivalSource;
        }
        else
        {
            this._etd = "-";
            this._pilotDeparture = "-";
            this._eta = "-";
            this._pilotArrival = "-";
        }

        this._onJobsChanged()
    }

    private _emitGsisEvent(name: string): void
    {
        this.dispatchEvent(new CustomEvent(name, { detail: this._scheduleEntry, bubbles: true, composed: true }));
    }



    private _onJobsChanged()
    {
        if (this._scheduleEntry)
        {
            this._recalculating = VoyageRoutingModel!.planHasActiveCalculations(this._scheduleEntry.id);
        }
        else
        {
            this._recalculating = false;
        }
    }

    private _onPlanChanged(evt: PlanChangedEvent) {
        if (
            this._scheduleEntry &&
            evt.detail.scheduleId === this._scheduleEntry.id
        ) {
            // Reload everything
            this._scheduleEntryChanged()
        }
    }
    
    private async _onRecalcParametersChanged(evt: CustomEvent): Promise<void>
    {
        //updating schedule card when the recalc parameters are changed
        if (this._scheduleEntry && evt.detail.plan.scheduleId === this._scheduleEntry.id)
        {
            try{
                const plan = evt.detail.plan;

                this._forwardDraft = plan.foreDraft  ? `${plan.foreDraft} m` : `${this._forwardDraft} m`;
                this._aftDraft = plan.aftDraft ? `${plan.aftDraft} m` : `${this._aftDraft} m`;
                this._GMt = plan.gmt ? `${plan.gmt} m` : `${this._GMt} m`;
                let departurePilotEtd: string|null = new Date(plan.departurePilotEtd).toISOString();
                let arrivalPilotEta: string|null = new Date(plan.arrivalPilotEta).toISOString();
                let eta: string|null = new Date(plan.eta).toISOString();
                let etd: string|null = new Date(plan.etd).toISOString();
                const timestampsAndTerminals = [
                    { timestamp: etd, terminalId: this._scheduleEntry.departureTerminalCode },
                    { timestamp: eta, terminalId: this._scheduleEntry.arrivalTerminalCode },
                    { timestamp: departurePilotEtd, terminalId: this._scheduleEntry.departureTerminalCode },
                    { timestamp: arrivalPilotEta, terminalId: this._scheduleEntry.arrivalTerminalCode }
                ];
                let conversionResult: string[] = [];
                if(isVesselOnCloud && !isOnline()) {
                    for (const timestampAndTerminal of timestampsAndTerminals) {
                        const result = await convertToLocalOffline(timestampAndTerminal.timestamp, timestampAndTerminal.terminalId);
                        if(result) {
                            conversionResult.push(result);
                        }
                    }
                } else {
                    conversionResult = await TerminalsModel!.getTerminalLocaltimeBatch(timestampsAndTerminals);
                }
                let idx = 0;
                this._etd = GetFormattedLocalDate(conversionResult[idx++]);
                this._eta = GetFormattedLocalDate(conversionResult[idx++]);
                this._pilotDeparture = departurePilotEtd ? GetFormattedLocalDate(conversionResult[idx++]) : "-";
                this._pilotArrival = arrivalPilotEta ? GetFormattedLocalDate(conversionResult[idx++]) : "-";
            }
            catch(err){
                console.log(err);
            }
        }
    }

    private _onTerminalsUpdated() {
        this.requestUpdate()
    }

    private _getDepartureScheduleSource(scheduleEntry: ScheduleEntry, isPilot? : boolean,): string
    {
        if (!isPilot && scheduleEntry.departurePortCall && scheduleEntry.departurePortCall.current.actuals && scheduleEntry.departurePortCall.current.actuals.departureAt)
        {
            return "actual";
        }
        return 'Sch'
    }
    
    private _getArrivalScheduleSource(scheduleEntry: ScheduleEntry, isPilot? : boolean,): string
    {
        if (!isPilot && scheduleEntry.arrivalPortCall && scheduleEntry.arrivalPortCall.current.actuals && scheduleEntry.arrivalPortCall.current.actuals.arrivalAt)
        {
            return "actual";
        }
        return 'Sch'
    }

    private _getPlanSource(scheduleTime: string, communicatedTime: string): string
    {
        if(new Date(scheduleTime).getTime() === new Date(communicatedTime).getTime()){
                return 'Sch';
            } else if(!communicatedTime || communicatedTime == '-'){
                return 'Sch';
            }
        return 'Com'
    }

    private _updateCurrentLegData()
    {
        this._remainingDataToPilot = VesselPerformanceModel!.getRemainingDataToPilot();
        this.requestUpdate();
    }

    private _getDeltaTimeText(number: number)
    {
        let seconds = number/1000;
 
        const days = Math.floor(seconds / (3600*24));
        seconds -= days*3600*24;
        const hours = Math.floor(seconds / 3600);
        seconds -= hours*3600;
        const minutes = Math.floor(seconds / 60);
        seconds -= minutes*60;
 
        if (hours < 1 && days < 1)
        {
            return `${minutes}`.padStart(2, "0") + "m";
        }
        
        if (days < 1)
        {
            return `${hours}`.padStart(2, "0") + "h" + ' ' + minutes.toFixed(0).padStart(2, "0") + "m";
        }
        
        return `${days}d ` + `${hours}`.padStart(2, "0") + "h" + ' ' + minutes.toFixed(0).padStart(2, "0") + "m";
    }

    private _checkIfBasicPlanAvailable(scheduleEntry: ScheduleEntry) {
        if (scheduleEntry.isBasicPlanAvailable) {
            VesselAlertsModel!.addAlert(isVesselOnCloud ? VoyageImoModel?.imo : (<any>window)["appenv"].vesselIMONumber, {
            message: "",
            id: `${scheduleEntry.id}`,
            type: AlertType.basicPlanAvailable,
        });
        } else {
            VesselAlertsModel!.clearAlerts(isVesselOnCloud ? VoyageImoModel?.imo : (<any>window)["appenv"].vesselIMONumber);
        }
    }

    private async _checkForDeclinedEtaRequest(e: CustomEvent){
        const declinedRequest = e.detail.alert;

        //if the current active schedule has a declined eta request from TBM
        if(declinedRequest){
            if(this._scheduleEntry?.active && this._scheduleEntry.id === declinedRequest?.data.schedule_id){
                const plan = await VesselPlanningModel!.getCurrentPlan(this._scheduleEntry.id);
                if(plan && plan.status == 'approved'){
                    this.hasDeclinedRequest = true;
                    this.showEtaRequest = true;
                    this.etaRequestText = '';
                    localStorage.removeItem('RemindLaterView');
                    return;
                }
            }
            return;
        } 
        this.hasDeclinedRequest = false;
    }

    private openEtaChangeModal(){
        this.dispatchEvent(
            new CustomEvent(ScheduleChangeEvents.ReviewChange, {
                bubbles: true,
                composed: true,
        }))
    }

    private async _checkIfPilotPointisPassed(schedule_id: string){
        const currentPlan = await VesselPlanningModel!.getCurrentPlan(schedule_id);
        if( !currentPlan) return;
        const route = currentPlan.routes[currentPlan.selectedRouteIndex];
        const pilotIndeces = getPilotPointIndeces(route);
        const arrivalPilotIndex = pilotIndeces[pilotIndeces.length-1];
        const currentLeg = route.route_geometry[arrivalPilotIndex];
        if (currentLeg && currentLeg.properties.isPassed){
            this.showEtaRequest = false;
            this.etaRequestText = EtaRequestDeactivated.PilotPointPassed;  
        }
    }
};
